import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, computed, inject, signal } from '@angular/core';
import { AssistantContextService } from '@shared/services/assistant-context.service';

import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin, of } from 'rxjs';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CardModule } from 'primeng/card';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { MenuItem } from 'primeng/api';
import { MultiSelectModule } from 'primeng/multiselect';
import { TooltipModule } from 'primeng/tooltip';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DividerModule } from 'primeng/divider';
import { TextareaModule } from 'primeng/textarea';
import { WebhooksService } from '@generated/services/webhooks.service';
import { AgentsService } from '@generated/services/agents.service';
import { AgentSummary } from '@generated/models/agent-summary';
import { WebhookDto } from '@generated/models/webhook-dto';
import { UpsertWebhookCommand } from '@generated/models/upsert-webhook-command';
import { EventType } from '@generated/models/event-type';
import { NotificationService } from '@core/services/notification.service';
import { quietErrors } from '@core/interceptors/error.interceptor';
import { AssistantSurfaceDirective } from '@shared/directives/assistant-surface.directive';
import { AssistantEditableDirective } from '@shared/directives/assistant-editable.directive';
import { AssistantActionDirective } from '@shared/directives/assistant-action.directive';
import { WEBHOOK_FIELDS } from '@core/assistant/assistant-catalog';

interface EventTypeInfo {
  label: string;
  description: string;
  /**
   * Событие принадлежит конкретному агенту, значит его можно сузить до
   * выбранных. Зеркалит EventTypeExtensions.SupportsAgentFilter на бэке:
   * для события без агента фильтр там игнорируется, и обещать его в UI
   * было бы враньём.
   */
  supportsAgentFilter: boolean;
}

@Component({
  selector: 'app-webhook-edit',
  imports: [
    AssistantActionDirective,
    AssistantSurfaceDirective,
    AssistantEditableDirective,
    FormsModule,
    ButtonModule,
    InputTextModule,
    CardModule,
    ToggleSwitchModule,
    BreadcrumbModule,
    MultiSelectModule,
    TooltipModule,
    ProgressSpinnerModule,
    DividerModule,
    TextareaModule
],
  templateUrl: './webhook-edit.component.html',
  styleUrl: './webhook-edit.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class WebhookEditComponent implements OnInit, OnDestroy {
  /** Идентификаторы полей для агента: опечатку ловит компилятор. */
  protected readonly F = WEBHOOK_FIELDS;

  readonly url = signal('');
  readonly description = signal<string | null>('');
  readonly eventTypes = signal<EventType[]>([]);
  readonly onlySpecifiedAgents = signal(false);
  readonly agentIds = signal<string[]>([]);
  readonly enabled = signal(true);
  readonly secret = signal<string | null>(null);

  readonly isLoading = signal(false);
  readonly isSaving = signal(false);
  readonly breadcrumbItems = signal<MenuItem[]>([]);

  private assistantContext = inject(AssistantContextService);

  readonly eventTypeMap: Record<EventType, EventTypeInfo> = {
    ConversationComplete: {
      label: $localize`:@@webhookEdit.event.conversationComplete.label:Conversation Complete`,
      description: $localize`:@@webhookEdit.event.conversationComplete.description:Triggered when a conversation with an agent ends`,
      supportsAgentFilter: true
    },
    ConversationStarted: {
      label: $localize`:@@webhookEdit.event.conversationStarted.label:Conversation Started`,
      description: $localize`:@@webhookEdit.event.conversationStarted.description:Triggered when a new conversation with an agent begins`,
      supportsAgentFilter: true
    },
    CallStarted: {
      label: $localize`:@@webhookEdit.event.callStarted.label:Call Started`,
      description: $localize`:@@webhookEdit.event.callStarted.description:Triggered when an inbound or outbound call begins`,
      supportsAgentFilter: true
    },
    CallFinished: {
      label: $localize`:@@webhookEdit.event.callFinished.label:Call Finished`,
      description: $localize`:@@webhookEdit.event.callFinished.description:Triggered when a call ends, with outcome and end reason`,
      supportsAgentFilter: true
    }
  };

  readonly availableEventTypes = Object.keys(this.eventTypeMap) as EventType[];

  private readonly agents = signal<AgentSummary[]>([]);

  readonly agentOptions = computed(() =>
    this.agents()
      .filter(agent => !!agent.id)
      .map(agent => ({
        label: agent.title?.trim() || $localize`:@@webhookEdit.agents.untitled:Untitled agent`,
        value: agent.id!
      }))
  );

  /**
   * Сколько выбранных ранее агентов не нашлось при загрузке: удалены или
   * из другого пространства. Такой id мультиселект не покажет, а бэк на
   * сохранении отклонит всю форму, поэтому выкидываем их сразу — и
   * говорим об этом, иначе фильтр тихо меняет смысл.
   */
  readonly droppedAgentCount = signal(0);

  /** Фильтр включён, но никого не выбрали — доставлять будет нечего. */
  readonly noAgentsPicked = computed(() => this.onlySpecifiedAgents() && this.agentIds().length === 0);

  /**
   * Подписка на событие, которое агентом не сужается: фильтр для него не
   * сработает, и честнее сказать об этом до сохранения, а не после.
   */
  readonly unfilterableEventLabels = computed(() =>
    this.eventTypes()
      .filter(eventType => !this.eventTypeMap[eventType]?.supportsAgentFilter)
      .map(eventType => this.eventTypeMap[eventType].label)
      .join(', ')
  );

  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private webhooksService = inject(WebhooksService);
  private agentsService = inject(AgentsService);
  private notificationService = inject(NotificationService);

  get webhookId(): string | null {
    return this.route.snapshot.paramMap.get('id');
  }

  get isEditMode(): boolean {
    return this.webhookId !== null;
  }

  get headerTitle(): string {
    return this.isEditMode
      ? $localize`:@@webhookEdit.header.edit:Edit Webhook`
      : $localize`:@@webhookEdit.header.create:Create Webhook`;
  }

  get saveBtnLabel(): string {
    if (this.isSaving()) return $localize`:@@common.saving:Saving...`;
    return this.isEditMode
      ? $localize`:@@common.save:Save`
      : $localize`:@@webhookEdit.header.create:Create Webhook`;
  }

  ngOnInit(): void {
    this.initBreadcrumb();
    this.load();
  }

  ngOnDestroy(): void {
    this.assistantContext.clear();
  }

  private publishAssistantContext(): void {
    this.assistantContext.set({
      description: 'Webhook editor',
      value: { type: 'webhook', id: this.webhookId, url: this.url() },
    });
  }

  private initBreadcrumb(): void {
    this.breadcrumbItems.set([
      { label: $localize`:@@nav.developers:Developers`, routerLink: '/developers' },
      {
        label: this.isEditMode
          ? $localize`:@@webhookEdit.header.edit:Edit Webhook`
          : $localize`:@@webhookEdit.breadcrumb.new:New Webhook`
      }
    ]);
  }

  /**
   * Агенты нужны обеим веткам — и созданию, и редактированию: без списка
   * мультиселект пуст, а уже выбранных агентов нечем показать.
   */
  private load(): void {
    const id = this.webhookId;
    this.isLoading.set(true);

    forkJoin({
      agents: this.agentsService.apiAgentsGet(),
      webhook: id ? this.webhooksService.apiWebhooksIdGet({ id }) : of(null)
    }).subscribe({
      next: ({ agents, webhook }) => {
        this.agents.set(agents.agents || []);

        if (webhook) {
          this.apply(webhook);
          this.publishAssistantContext();
        }

        this.isLoading.set(false);
      },
      error: () => {
        this.notificationService.showError(
          id
            ? $localize`:@@webhookEdit.notify.loadFail:Failed to load webhook`
            : $localize`:@@webhookEdit.notify.agentsLoadFail:Failed to load agents`
        );
        this.isLoading.set(false);

        if (id) {
          this.router.navigate(['/developers']);
        }
      }
    });
  }

  private apply(webhook: WebhookDto): void {
    this.url.set(webhook.url || '');
    this.description.set(webhook.description ?? '');
    this.eventTypes.set(webhook.event_types || []);
    this.onlySpecifiedAgents.set(webhook.only_specified_agents || false);
    this.enabled.set(webhook.enabled ?? true);
    this.secret.set(webhook.secret || null);

    const stored = webhook.agent_ids || [];
    const known = new Set(this.agents().map(agent => agent.id));
    const alive = stored.filter(id => known.has(id));

    this.agentIds.set(alive);
    this.droppedAgentCount.set(stored.length - alive.length);
  }

  isEventSelected(eventType: EventType): boolean {
    return this.eventTypes().includes(eventType);
  }

  toggleEvent(eventType: EventType): void {
    this.eventTypes.update(types =>
      types.includes(eventType) ? types.filter(type => type !== eventType) : [...types, eventType]
    );
  }

  onSave(): void {
    if (this.isSaving()) return;

    if (!this.url().trim()) {
      this.notificationService.showError($localize`:@@webhookTool.notify.urlRequired:Please enter a URL`);
      return;
    }

    if (this.noAgentsPicked()) {
      this.notificationService.showError(
        $localize`:@@webhookEdit.notify.agentsRequired:Select at least one agent or turn the agent filter off`
      );
      return;
    }

    this.isSaving.set(true);

    const command: UpsertWebhookCommand = {
      id: this.webhookId,
      url: this.url(),
      description: this.description(),
      event_types: this.eventTypes(),
      only_specified_agents: this.onlySpecifiedAgents(),
      agent_ids: this.agentIds(),
      enabled: this.enabled()
    };

    // quietErrors: отказ по фильтру агентов приезжает 400 с причиной в
    // detail, и общий тост интерсептора лёг бы вторым поверх нашего.
    this.webhooksService.apiWebhooksPost({ body: command }, quietErrors()).subscribe({
      next: (result) => {
        this.notificationService.showSuccess(
          this.isEditMode
            ? $localize`:@@webhookEdit.notify.updated:Webhook updated`
            : $localize`:@@webhookEdit.notify.created:Webhook created`
        );
        this.isSaving.set(false);

        if (!this.isEditMode && result.id) {
          this.router.navigate(['/developers/webhooks', result.id]);
        } else if (this.isEditMode) {
          this.load();
        }
      },
      error: (failure) => {
        this.notificationService.showError(this.saveFailureMessage(failure));
        this.isSaving.set(false);
      }
    });
  }

  /**
   * Почему не сохранилось. Отказ по фильтру агентов приезжает 400 с текстом
   * в `detail` — показать вместо него «не удалось сохранить» значит
   * отправить человека гадать, что именно не так с формой.
   */
  private saveFailureMessage(failure: unknown): string {
    const body = (failure as { error?: { detail?: string; error?: string } })?.error;
    const detail = body?.detail || body?.error;

    if (!detail) {
      return $localize`:@@webhookEdit.notify.saveFail:Failed to save webhook`;
    }

    if (/at least one agent/i.test(detail)) {
      return $localize`:@@webhookEdit.notify.agentsRequired:Select at least one agent or turn the agent filter off`;
    }

    if (/belong to this workspace/i.test(detail)) {
      return $localize`:@@webhookEdit.notify.agentsForeign:Some of the selected agents are no longer in this workspace. Reopen the webhook to refresh the list.`;
    }

    return detail;
  }

  onCancel(): void {
    this.router.navigate(['/developers']);
  }

  async copySecret(): Promise<void> {
    const secret = this.secret();
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret);
      this.notificationService.showSuccess($localize`:@@webhookEdit.notify.secretCopied:Secret copied to clipboard`);
    } catch {
      const textArea = document.createElement('textarea');
      textArea.value = secret;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      this.notificationService.showSuccess($localize`:@@webhookEdit.notify.secretCopied:Secret copied to clipboard`);
    }
  }
}
